import React, { useEffect, useState, useCallback } from 'react';
import { getDashboardStats } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Megaphone, TrendingUp, DollarSign, Clock, Zap } from 'lucide-react';
import { DateFilter, StatCard, CurrencyStatCard } from '../components/dashboard/DashboardWidgets';

const BUY_TYPE_COLORS = {
  CPM: '#2563EB', CPC: '#7C3AED', CPS: '#059669', CPV: '#D97706',
  CPCV: '#DC2626', CPA: '#0891B2', CPL: '#4F46E5', other: '#71717A',
};
const STATUS_COLORS = { active: '#10B981', draft: '#71717A', paused: '#F59E0B', completed: '#3B82F6' };
const CURRENCY_SYMBOLS = { USD: '$', INR: '\u20B9', MYR: 'RM', AED: 'AED', SGD: 'S$' };
const CHART_TICK_STYLE = { fontSize: 12, fill: '#71717A' };
const CHART_TOOLTIP_STYLE = { border: '1px solid #E4E4E7', borderRadius: 0, fontSize: 13 };

function getDateRange(preset) {
  const now = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  if (preset === 'all') return { date_from: null, date_to: null };
  if (preset === 'this_month') return { date_from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: fmt(now) };
  if (preset === 'last_month') return { date_from: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)), date_to: fmt(new Date(now.getFullYear(), now.getMonth(), 0)) };
  const days = parseInt(preset);
  if (!isNaN(days)) { const from = new Date(now); from.setDate(from.getDate() - days); return { date_from: fmt(from), date_to: fmt(now) }; }
  return { date_from: null, date_to: null };
}

function formatCurrencyList(list) {
  if (!list || list.length === 0) return [{ label: '-', value: 0 }];
  return list.map(item => ({ label: `${CURRENCY_SYMBOLS[item.currency] || item.currency || '$'}${item.amount.toLocaleString()}`, currency: item.currency }));
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      let params = {};
      if (preset === 'custom') {
        if (customFrom) params.date_from = customFrom;
        if (customTo) params.date_to = customTo;
      } else {
        const range = getDateRange(preset);
        if (range.date_from) params.date_from = range.date_from;
        if (range.date_to) params.date_to = range.date_to;
      }
      const res = await getDashboardStats(params);
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [preset, customFrom, customTo]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handlePresetChange = (val) => {
    setPreset(val);
    if (val !== 'custom') { setCustomFrom(''); setCustomTo(''); }
  };

  if (loading && !stats) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-8 bg-zinc-100 animate-pulse w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">{[1,2,3,4].map(i => <div key={i} className="h-28 bg-zinc-100 animate-pulse" />)}</div>
      </div>
    );
  }
  if (!stats) return null;

  const buyTypeData = stats.buy_type_breakdown || [];
  const revenueData = stats.revenue_by_client || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Dashboard</h1>
        <DateFilter preset={preset} onPresetChange={handlePresetChange} customFrom={customFrom} customTo={customTo} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo} loading={loading} />
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={Users} accent="bg-zinc-950" value={stats.total_clients} label="Total Clients" testId="stat-total-clients" />
        <StatCard icon={Megaphone} accent="bg-zinc-950" value={stats.total_campaigns} label="Total Campaigns" testId="stat-total-campaigns" />
        <StatCard icon={Zap} accent="bg-emerald-600" value={stats.active_campaigns} label="Active Campaigns" testId="stat-active-campaigns" />
        <CurrencyStatCard icon={TrendingUp} accent="bg-zinc-950" items={formatCurrencyList(stats.budget_by_currency)} label="Total Budget" testId="stat-total-budget" />
        <CurrencyStatCard icon={DollarSign} accent="bg-emerald-600" items={formatCurrencyList(stats.revenue_by_currency)} label="Total Revenue" testId="stat-total-revenue" />
        <CurrencyStatCard icon={Clock} accent="bg-amber-500" items={formatCurrencyList(stats.draft_by_currency)} label="Draft Budget" testId="stat-draft-budget" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 p-6" data-testid="revenue-chart">
          <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mb-4">Budget by Client</h3>
          {revenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="client" tick={CHART_TICK_STYLE} />
                <YAxis tick={CHART_TICK_STYLE} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(value) => [`$${value.toLocaleString()}`, 'Budget']} />
                <Bar dataKey="revenue" fill="#000000" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-sm text-zinc-400">No data for selected period</div>}
        </div>
        <div className="bg-white border border-zinc-200 p-6" data-testid="buy-type-chart">
          <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mb-4">Campaigns by Buy Type</h3>
          {buyTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={buyTypeData} dataKey="count" nameKey="buy_type" cx="50%" cy="50%" outerRadius={100} label={({ buy_type, count }) => `${buy_type} (${count})`}>
                  {buyTypeData.map((entry) => <Cell key={entry.buy_type} fill={BUY_TYPE_COLORS[entry.buy_type] || BUY_TYPE_COLORS.other} />)}
                </Pie>
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[280px] flex items-center justify-center text-sm text-zinc-400">No campaigns for selected period</div>}
        </div>
      </div>

      {/* Recent Campaigns */}
      <div className="bg-white border border-zinc-200 p-6" data-testid="recent-campaigns">
        <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mb-4">Recent Campaigns</h3>
        {(stats.recent_campaigns || []).length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Campaign</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Client</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Buy Type</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Budget</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Volume</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Price</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_campaigns.map((c) => {
                  const sym = CURRENCY_SYMBOLS[c.currency] || c.currency || '$';
                  return (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150">
                      <td className="px-4 py-3 font-medium text-zinc-900">{c.name}</td>
                      <td className="px-4 py-3 text-zinc-600">{c.client_name}</td>
                      <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 text-xs font-bold" style={{ backgroundColor: (BUY_TYPE_COLORS[c.buy_type] || '#71717A') + '18', color: BUY_TYPE_COLORS[c.buy_type] || '#71717A' }}>{c.buy_type || '-'}</span></td>
                      <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: (STATUS_COLORS[c.status] || '#71717A') + '20', color: STATUS_COLORS[c.status] || '#71717A' }}>{c.status}</span></td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-900">{sym}{(c.budget || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-900">{(c.volume || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-mono text-zinc-900">{sym}{(c.price || 0).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <div className="text-sm text-zinc-400 py-8 text-center">No campaigns for selected period</div>}
      </div>
    </div>
  );
}
